<template>
  <div class="dashboard-empleado-container p-4">
    <div class="mb-3">
      <vs-button icon="arrow_back" size="small" type="border" @click="$router.push('/actividades-control')">Volver</vs-button>
    </div>
    <vx-card title="Mi Dashboard de Actividades">
      <!-- Filtros de período -->
      <div class="flex flex-wrap items-center mb-6 gap-3">
        <div class="w-full md:w-1/4">
          <label class="text-sm font-semibold">Fecha Inicio</label>
          <flat-pickr
            v-model="filtros.fecha_inicio"
            :config="configdateTimePicker"
            placeholder="Fecha inicio"
            class="w-full"
            @on-change="cargarDashboard"
          />
        </div>
        <div class="w-full md:w-1/4">
          <label class="text-sm font-semibold">Fecha Fin</label>
          <flat-pickr
            v-model="filtros.fecha_fin"
            :config="configdateTimePicker"
            placeholder="Fecha fin"
            class="w-full"
            @on-change="cargarDashboard"
          />
        </div>
        <div class="w-full md:w-1/4 flex items-end gap-2">
          <vs-button @click="setPeriodoMes" size="small">Este Mes</vs-button>
          <vs-button @click="setPeriodoAnio" size="small">Este Año</vs-button>
        </div>
      </div>

      <!-- Tarjetas de resumen -->
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div class="stat-card bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-lg shadow-lg">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm opacity-80">Total Tareas</p>
              <h3 class="text-3xl font-bold mt-1">{{ dashboardData.total_tareas || 0 }}</h3>
            </div>
            <vs-icon icon="assignment" size="large" class="opacity-50" />
          </div>
        </div>

        <div class="stat-card bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-lg shadow-lg">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm opacity-80">Actividades</p>
              <h3 class="text-3xl font-bold mt-1">{{ dashboardData.total_actividades || 0 }}</h3>
            </div>
            <vs-icon icon="check_circle" size="large" class="opacity-50" />
          </div>
        </div>

        <div class="stat-card bg-gradient-to-r from-purple-500 to-purple-600 text-white p-6 rounded-lg shadow-lg">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm opacity-80">Validadas</p>
              <h3 class="text-3xl font-bold mt-1">
                {{ (dashboardData.resumen_estados && dashboardData.resumen_estados.validada && dashboardData.resumen_estados.validada.count) || 0 }}
              </h3>
            </div>
            <vs-icon icon="verified" size="large" class="opacity-50" />
          </div>
        </div>

        <div class="stat-card bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6 rounded-lg shadow-lg">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm opacity-80">Fin de Semana</p>
              <h3 class="text-3xl font-bold mt-1">{{ dashboardData.tareas_fin_semana || 0 }}</h3>
            </div>
            <vs-icon icon="weekend" size="large" class="opacity-50" />
          </div>
        </div>
      </div>

      <!-- Estados de tareas -->
      <div class="mb-6">
        <h3 class="font-bold text-lg mb-4">Resumen por Estado</h3>
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div
            v-for="(estado, key) in dashboardData.resumen_estados"
            :key="key"
            class="border rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-600">{{ getEstadoTexto(key) }}</p>
                <p class="text-2xl font-bold mt-1">{{ estado.count }}</p>
                <p class="text-sm text-gray-500">{{ estado.total_cantidad }} actividades</p>
              </div>
              <vs-icon :icon="getEstadoIcon(key)" :color="getEstadoColor(key)" size="large" />
            </div>
          </div>
        </div>
      </div>

      <!-- Resumen por categorías -->
      <div class="mb-6">
        <h3 class="font-bold text-lg mb-4">Resumen por Categoría</h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div
            v-for="(categoria, key) in dashboardData.resumen_categorias"
            :key="key"
            class="border rounded-lg p-4 hover:shadow-md transition-shadow"
          >
            <p class="font-semibold text-primary">{{ categoria.categoria }}</p>
            <div class="mt-2">
              <span class="text-2xl font-bold">{{ categoria.count }}</span>
              <span class="text-sm text-gray-500 ml-2">tareas</span>
            </div>
            <div class="mt-1">
              <span class="text-lg font-semibold">{{ categoria.total_cantidad }}</span>
              <span class="text-sm text-gray-500 ml-2">actividades</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Cumplimiento de KPIs -->
      <div v-if="dashboardData.cumplimiento_kpis && dashboardData.cumplimiento_kpis.length" class="mb-6">
        <h3 class="font-bold text-lg mb-4">Cumplimiento de KPIs</h3>
        <vs-table :data="dashboardData.cumplimiento_kpis" stripe>
          <template slot="thead">
            <vs-th>Categoría</vs-th>
            <vs-th>Promedio Diario</vs-th>
            <vs-th>Meta Mínima</vs-th>
            <vs-th>Meta Máxima</vs-th>
            <vs-th>Días Trabajados</vs-th>
            <vs-th>Total Período</vs-th>
            <vs-th>Estado</vs-th>
          </template>

          <template slot-scope="{ data }">
            <vs-tr v-for="(kpi, index) in data" :key="index">
              <vs-td>{{ kpi.categoria }}</vs-td>
              <vs-td>
                <span class="font-bold">{{ kpi.promedio_diario }}</span>
              </vs-td>
              <vs-td>{{ kpi.limite_diario_minimo }}</vs-td>
              <vs-td>{{ kpi.limite_diario_maximo || '-' }}</vs-td>
              <vs-td>{{ kpi.dias_trabajados }}</vs-td>
              <vs-td>{{ kpi.total_periodo }}</vs-td>
              <vs-td>
                <vs-chip :color="getCumplimientoColor(kpi)">
                  {{ getCumplimientoTexto(kpi) }}
                </vs-chip>
              </vs-td>
            </vs-tr>
          </template>
        </vs-table>
      </div>

      <!-- Gráfico de tendencia (opcional, requiere Chart.js) -->
      <div class="mt-6">
        <h3 class="font-bold text-lg mb-4">Período Analizado</h3>
        <div class="bg-gray-100 p-4 rounded">
          <p><strong>Desde:</strong> {{ formatDate(dashboardData.periodo && dashboardData.periodo.fecha_inicio) }}</p>
          <p><strong>Hasta:</strong> {{ formatDate(dashboardData.periodo && dashboardData.periodo.fecha_fin) }}</p>
        </div>
      </div>
    </vx-card>
  </div>
</template>

<script>
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import { Spanish } from 'flatpickr/dist/l10n/es.js'
import { mapState } from 'vuex'

export default {
  name: 'DashboardEmpleado',
  components: {
    flatPickr
  },
  data() {
    return {
      dashboardData: {},
      filtros: {
        fecha_inicio: this.getFirstDayOfMonth(),
        fecha_fin: this.getLastDayOfMonth()
      },
      configdateTimePicker: {
        locale: Spanish,
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'd/m/Y'
      }
    }
  },
  computed: {
    ...mapState(['auth', 'userRoot', 'susuario']),
    usuarioId() {
      return this.auth ? this.auth.idusuario : null
    }
  },
  mounted() {
    this.cargarDashboard()
  },
  methods: {
    async cargarDashboard() {
      this.$vs.loading()
      try {
        const response = await this.$http.get(
          this.$server_url + `actividades-control/dashboard/empleado/${this.usuarioId}`,
          {
            params: {
              fecha_inicio: this.filtros.fecha_inicio,
              fecha_fin: this.filtros.fecha_fin
            }
          }
        )
        this.dashboardData = response.data || {}
      } catch (error) {
        console.error('Error al cargar dashboard:', error)
        this.$vs.notify({
          title: 'Error',
          text: 'No se pudo cargar el dashboard',
          color: 'danger'
        })
      } finally {
        this.$vs.loading.close()
      }
    },
    setPeriodoMes() {
      this.filtros.fecha_inicio = this.getFirstDayOfMonth()
      this.filtros.fecha_fin = this.getLastDayOfMonth()
      this.cargarDashboard()
    },
    setPeriodoAnio() {
      const now = new Date()
      this.filtros.fecha_inicio = `${now.getFullYear()}-01-01`
      this.filtros.fecha_fin = `${now.getFullYear()}-12-31`
      this.cargarDashboard()
    },
    getFirstDayOfMonth() {
      const now = new Date()
      return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0]
    },
    getLastDayOfMonth() {
      const now = new Date()
      return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0]
    },
    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })
    },
    getEstadoTexto(estado) {
      const textos = {
        pendiente: 'Pendientes',
        en_revision: 'En Revisión',
        validada: 'Validadas',
        rechazada: 'Rechazadas'
      }
      return textos[estado] || estado
    },
    getEstadoIcon(estado) {
      const icons = {
        pendiente: 'schedule',
        en_revision: 'visibility',
        validada: 'check_circle',
        rechazada: 'cancel'
      }
      return icons[estado] || 'info'
    },
    getEstadoColor(estado) {
      const colores = {
        pendiente: 'warning',
        en_revision: 'primary',
        validada: 'success',
        rechazada: 'danger'
      }
      return colores[estado] || 'dark'
    },
    getCumplimientoColor(kpi) {
      if (kpi.promedio_diario >= kpi.limite_diario_minimo) {
        if (kpi.limite_diario_maximo && kpi.promedio_diario > kpi.limite_diario_maximo) {
          return 'warning'
        }
        return 'success'
      }
      return 'danger'
    },
    getCumplimientoTexto(kpi) {
      if (kpi.promedio_diario >= kpi.limite_diario_minimo) {
        if (kpi.limite_diario_maximo && kpi.promedio_diario > kpi.limite_diario_maximo) {
          return 'Sobre el límite'
        }
        return 'Cumpliendo'
      }
      return 'Bajo el mínimo'
    }
  }
}
</script>

<style scoped>
.dashboard-empleado-container {
  max-width: 1400px;
  margin: 0 auto;
}

.stat-card {
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
}
</style>
